import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';

import { AdminPageHeader } from '@/components/admin/admin-page-header';
import { FilterBar } from '@/components/admin/filter-bar';
import { InvitationCreate } from '@/components/admin/invitations/invitation-create/invitation-create';
import { BulkDeleteBar } from '@/components/admin/shared/bulk-delete-bar';
import { OrganizationPicker } from '@/components/admin/shared/organization-picker';
import { useBulkSelection } from '@/components/admin/shared/use-bulk-selection';
import { UsersTable } from '@/components/admin/users/users-table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { meQueryOptions } from '@/hooks/data/use-auth';
import {
  useAdminUsers,
  useBulkDeleteUsers,
} from '@/hooks/data/admin/use-users';
import { useDebounced } from '@/hooks/primitives/use-debounced';
import { Role } from '@/lib/roles';
import type { UserRole } from '@/types/generated';
import { loadNamespaces } from '@/lib/i18n/i18n';

const searchSchema = z.object({
  organization_id: z.number().int().positive().optional(),
  role: z.enum(['SUPER_ADMIN', 'ADMIN', 'STANDARD', 'VIEWER']).optional(),
  is_active: z.boolean().optional(),
  q: z.string().optional(),
  offset: z.number().int().nonnegative().default(0),
});

const LIMIT = 20;

interface RoleFilterProps {
  value: UserRole | undefined;
  onChange: (value: string) => void;
}

function RoleFilter({ value, onChange }: RoleFilterProps): React.ReactElement {
  return (
    <Select value={value ?? 'ALL'} onValueChange={onChange}>
      <SelectTrigger className="w-40">
        <SelectValue placeholder="Role" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="ALL">All roles</SelectItem>
        <SelectItem value="SUPER_ADMIN">Super admin</SelectItem>
        <SelectItem value="ADMIN">Admin</SelectItem>
        <SelectItem value="STANDARD">Standard</SelectItem>
        <SelectItem value="VIEWER">Viewer</SelectItem>
      </SelectContent>
    </Select>
  );
}

export const Route = createFileRoute('/_authenticated/admin/users/')({
  loader: () => loadNamespaces('settings'),
  validateSearch: searchSchema,
  staticData: { title: 'Users' },
  component: AdminUsersListPage,
});

function AdminUsersListPage(): React.ReactElement {
  const { organization_id, role, is_active, q, offset } = Route.useSearch();
  const navigate = Route.useNavigate();
  const [inviteOpen, setInviteOpen] = useState(false);
  const { data: currentUser } = useQuery(meQueryOptions);
  const bulkDelete = useBulkDeleteUsers();

  const [searchInput, setSearchInput] = useState(q ?? '');
  const debouncedSearch = useDebounced(searchInput, 300);

  const usersQuery = useAdminUsers({
    organization_id,
    role,
    is_active,
    email: debouncedSearch || undefined,
    limit: LIMIT,
    offset,
  });

  const bulk = useBulkSelection({
    rows: usersQuery.data?.items ?? [],
    getId: (user) => user.id,
    getLabel: (user) => user.email,
    isSelectable: (user) =>
      user.id !== currentUser?.id && user.role !== Role.SUPER_ADMIN,
  });

  const setOrgId = (value: number | undefined): void => {
    void navigate({
      search: (s) => ({ ...s, organization_id: value, offset: 0 }),
    });
  };

  const setRole = (value: string): void => {
    void navigate({
      search: (s) => ({
        ...s,
        role: value === 'ALL' ? undefined : (value as UserRole),
        offset: 0,
      }),
    });
  };

  const setActive = (value: string): void => {
    void navigate({
      search: (s) => ({
        ...s,
        is_active:
          value === 'ALL' ? undefined : value === 'true' ? true : false,
        offset: 0,
      }),
    });
  };

  const onSearchChange = (value: string): void => {
    setSearchInput(value);
    void navigate({
      search: (s) => ({ ...s, q: value || undefined, offset: 0 }),
    });
  };

  return (
    <main>
      <AdminPageHeader
        title="Users"
        description="Manage users across all organizations"
        actions={
          <Button onClick={() => setInviteOpen(true)}>Invite user</Button>
        }
      />
      <InvitationCreate
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        defaultOrgId={organization_id}
      />
      <FilterBar>
        <OrganizationPicker value={organization_id} onChange={setOrgId} />
        <RoleFilter value={role} onChange={setRole} />
        <Select
          value={is_active === undefined ? 'ALL' : String(is_active)}
          onValueChange={setActive}
        >
          <SelectTrigger className="w-36">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All statuses</SelectItem>
            <SelectItem value="true">Active</SelectItem>
            <SelectItem value="false">Inactive</SelectItem>
          </SelectContent>
        </Select>
        <Input
          placeholder="Search by email…"
          value={searchInput}
          onChange={(e) => onSearchChange(e.target.value)}
          className="w-64"
        />
      </FilterBar>
      <BulkDeleteBar
        items={bulk.items}
        onClear={bulk.clear}
        onSelectionChange={bulk.setSelectedIds}
        resource="user"
        isPending={bulkDelete.isPending}
        onDelete={bulkDelete.mutateAsync}
      />
      <UsersTable
        query={usersQuery}
        offset={offset}
        limit={LIMIT}
        onPageChange={(newOffset) =>
          void navigate({ search: (s) => ({ ...s, offset: newOffset }) })
        }
        selection={bulk.selection}
      />
    </main>
  );
}
